import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleYesterdayProviderActuals,
  type HuddleYesterdayProviderActualsResult,
  getHuddleYesterdayProviderActualsOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleProviderBreakdownToLeaderboardRows,
  mapHuddleProviderBreakdownToTableRows,
  mapHuddleProviderSummaryToMetrics,
  type HuddleProviderDetailTableRow,
  type HuddleProviderLeaderboardRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleYesterdayProviderActualsOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleYesterdayProviderActualsResult = {
  data: HuddleYesterdayProviderActualsResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  providerBreakdownRows: HuddleProviderDetailTableRow[]
  leaderboardRows: HuddleProviderLeaderboardRow[]
}

function useHuddleYesterdayProviderActualsFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleYesterdayProviderActualsOptions & {
  fetcher: HuddleDateFetcher<HuddleYesterdayProviderActualsResult>
}): UseHuddleYesterdayProviderActualsResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load yesterday provider actuals',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleProviderSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const providerBreakdownRows = useMemo(
    () => mapHuddleProviderBreakdownToTableRows(data?.providerBreakdown),
    [data?.providerBreakdown],
  )

  const leaderboardRows = useMemo(
    () => mapHuddleProviderBreakdownToLeaderboardRows(data?.providerBreakdown),
    [data?.providerBreakdown],
  )

  return {
    data,
    loading,
    error,
    summaryMetrics,
    providerBreakdownRows,
    leaderboardRows,
  }
}

export function useHuddleYesterdayProviderActuals(
  options: UseHuddleYesterdayProviderActualsOptions,
): UseHuddleYesterdayProviderActualsResult {
  return useHuddleYesterdayProviderActualsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayProviderActuals,
  })
}
export function useHuddleYesterdayProviderActualsOverview(
  options: UseHuddleYesterdayProviderActualsOptions,
): UseHuddleYesterdayProviderActualsResult {
  return useHuddleYesterdayProviderActualsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayProviderActualsOverview,
  })
}
